import React, { useEffect, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

type UsageItem = {
  id: string;
  userId: string;
  name: string;
  phone: string;
  whatsappUrl: string | null;
  startedAt: number;
  endedAt: number;
  hours: number;
};

function todayInputValue(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
}

function formatWhen(ms: number): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AdminPlatformUsagePanel: React.FC = () => {
  const [date, setDate] = useState(todayInputValue);
  const [items, setItems] = useState<UsageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const callable = httpsCallable<{ date: string }, { items: UsageItem[] }>(functions, "listPlatformUsage");
        const result = await callable({ date });
        if (!cancelled) setItems(result.data.items || []);
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Não foi possível carregar o uso.";
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [date]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-800 sm:text-xl">
          <Clock className="h-5 w-5 text-blue-600" />
          Quem usou
        </h2>
        <p className="text-sm text-gray-500">
          Pessoas que entraram na sala neste dia. O WhatsApp abre a conversa, sem mensagem pronta.
        </p>
      </div>

      <label className="block text-sm text-gray-700">
        Data
        <input
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </label>

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando...
        </div>
      ) : error ? (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          Ninguém usou a sala neste dia.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase text-gray-500">
              <tr>
                <th className="px-2 py-2">Pessoa</th>
                <th className="px-2 py-2">Início</th>
                <th className="px-2 py-2">Fim</th>
                <th className="px-2 py-2">Horas</th>
                <th className="px-2 py-2">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.userId}-${item.id}`} className="border-t border-gray-100">
                  <td className="px-2 py-3 font-medium text-gray-900">{item.name}</td>
                  <td className="px-2 py-3 whitespace-nowrap">{formatWhen(item.startedAt)}</td>
                  <td className="px-2 py-3 whitespace-nowrap">{formatWhen(item.endedAt)}</td>
                  <td className="px-2 py-3">{item.hours.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}</td>
                  <td className="px-2 py-3">
                    {item.whatsappUrl ? (
                      <a
                        href={item.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#128C7E] hover:underline"
                      >
                        Abrir WhatsApp
                      </a>
                    ) : (
                      <span className="text-gray-400">Sem telefone</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPlatformUsagePanel;
